import React from 'react'

import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { AsyncSelect, Grid, TextField, Typography } from '@the-platform-group/component-library'
import { Error as ErrorIcon } from '@the-platform-group/component-library/Icons'
import { formatDate } from '@the-platform-group/formatters/dateFormat'

import { ListPaper } from 'components/Paper'
import FileUploadModalOpenButton from './components/FileUploadModalOpenButton'
import Modal from 'components/Modal'

import useReports from './hooks/useReports'
import { useDeleteReport, useDeleteConfirmation } from './hooks/useDeleteReport'

import { debouncedLoadCustomers } from 'utils/optionsLoaders'
import { getColumnValues, getRowValues } from './ReportTableValues'

const DeleteConfirmationModal = ({
  canDelete,
  isDeleting,
  error,
  open,
  onClose,
  onDelete,
  onChangeConfirmText,
  reportToDelete = {},
}) => {
  const { id, customer, cusip, date } = reportToDelete
  const title = (
    <Typography variant="subtitle2" component="h6" display="flex" alignItems="center" gap={1}>
      <ErrorIcon fontSize="large" color="error" />
      Delete NOBO report for customer {customer} reported on {formatDate(date)}?
      <br />
      Type its CUSIP [{cusip}] below to confirm deletion.
    </Typography>
  )

  return (
    <Modal
      handleClose={onClose}
      handleSave={canDelete ? () => onDelete(id) : undefined}
      isLoading={isDeleting}
      open={open}
      saveButtonText="Delete"
      title={title}
    >
      <TextField
        error={Boolean(error)}
        fullWidth
        onChange={onChangeConfirmText}
        placeholder="CUSIP"
        helperText={error ? 'Error deleting report' : ''}
      />
    </Modal>
  )
}

/**
 * Component for displaying NOBO reports in a data grid with filtering capabilities.
 *
 * This component provides the following features:
 * - Displays NOBO reports in a paginated data grid
 * - Allows filtering reports by customer name
 * - Includes file upload functionality when enabled via feature flag
 * - Includes delete report functionality with confirmation modal
 * - Supports pagination and adjustable page size
 *
 * @returns {React.ReactElement} The rendered NOBO reports component
 */
const ReportListComponent = () => {
  const {
    reports,
    isLoading,
    pagination,
    filters,
    handlePageChange,
    handleRowsChange,
    handleCustomerSearch,
  } = useReports()

  const {
    isOpen,
    onOpen: openDeleteConfirmationModal,
    onClose,
    reportToDelete,
    onChangeConfirmText,
    canDelete,
  } = useDeleteConfirmation()
  const { deleteReport, error, isDeleting } = useDeleteReport({ onClose })

  const { page, itemCount, pageSize } = pagination
  const optionsLoader = term => debouncedLoadCustomers({ term, state: 'active' })

  return (
    <>
      <Typography variant="h4" component="h1">
        NOBO Reports
      </Typography>
      <Grid container direction="column" spacing={2}>
        <Grid item container alignItems="center">
          <Grid item xs={6}>
            <FileUploadModalOpenButton />
          </Grid>
          <Grid item justifyContent="right" xs={6}>
            <AsyncSelect
              id="customersFilter"
              placeholder="Search customers"
              value={filters.customerSearch}
              onChange={handleCustomerSearch}
              optionsLoader={optionsLoader}
              hideArrow
              includeClearAll
            />
          </Grid>
        </Grid>
        <Grid item>
          <ListPaper>
            <DataGrid
              columns={getColumnValues({ onDeleteReport: openDeleteConfirmationModal })}
              commonColumnProps={{ align: 'center', headerAlign: 'center' }}
              rows={getRowValues(reports)}
              loading={isLoading}
              localeText={{
                noRowsLabel:
                  'Looks like nothing matches your search. Try removing some criteria or attempt a new search.',
              }}
              name="noboReports"
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handleRowsChange}
              rowCount={itemCount}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </ListPaper>
        </Grid>
      </Grid>
      <DeleteConfirmationModal
        canDelete={canDelete}
        error={error}
        isDeleting={isDeleting}
        open={isOpen}
        onClose={onClose}
        onDelete={deleteReport}
        reportToDelete={reportToDelete}
        onChangeConfirmText={onChangeConfirmText}
      />
    </>
  )
}

export default ReportListComponent
